import React, { useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { UPSERT_ASSET_FORM_ID } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { useMiscAssetsForm } from './useMiscAssetsForm';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { MiscAssetDTO } from '../../tables/MiscAssets/MiscAssets.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { AssetBucketSelect } from '../components/AssetBucketSelect/AssetBucketSelect';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { GrossEstateCheckbox } from '../components/GrossEstateCheckbox/GrossEstateCheckbox';
import { getIsOwnerTrust } from '../utils/getIsOwnerTrust';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { MiscAssetsFormValues } from './MiscAssetsForm.meta';
import { APPEND_ASSET_OWNER, OwnerModelFormDTO } from '../../OtherAssets.meta';
import { AssetOwnership } from '../components/AssetOwnership/AssetOwnership';

export interface Props {
  clientId: string;
  asset: MiscAssetDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  isEstatePlanningWorkflow: boolean;
}

export const MiscAssetsForm = ({
  clientId,
  options,
  asset,
  onSuccess,
  isEstatePlanningWorkflow,
}: Props) => {
  const {
    data: {
      formBag: {
        register,
        control,
        formState: { errors },
        watch,
        setValue,
      },
      mutationBag,
      ownerOptions,
      saleYearOptions,
      defaultShowAdvanced,
      assetBucketOptions,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      allowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: { onSubmit },
  } = useMiscAssetsForm({ clientId, options, asset, onSuccess });

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const [ownerModel, managementStyleSelection] = watch(['ownerModel', 'orionManagementStyleId']);

  const isOwnerTrust =
    ownerModel.length === 1 ? getIsOwnerTrust(ownerModel[0].Name ?? '', ownerOptions) : false;

  const { fields, update, append, remove } = useFieldArray<
    MiscAssetsFormValues,
    'ownerModel',
    'fieldKey'
  >({
    control,
    name: 'ownerModel',
    keyName: 'fieldKey',
  });

  const watchOwner = useWatch({ control, name: 'ownerModel' });

  const ownerModelFields = fields.map((field, index) => ({
    ...field,
    ...watchOwner[index],
  }));

  const updateOwner = (index: number, data: OwnerModelFormDTO) => {
    update(index, {
      ...ownerModelFields[index],
      ...data,
    });
  };

  const setPercentage = (percentArr: number[]) => {
    ownerModelFields.map((field, index) => {
      setValue(`ownerModel.${index}.Percentage`, percentArr[index].toString());
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_ASSET_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.space['16']}px;
      `}
      aria-label="Miscellaneous assets form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Input
        label="Name"
        formVariant={errors.name && 'error'}
        helperText={errors.name?.message}
        maxLength={32}
        {...register('name')}
      />

      <AssetOwnership<MiscAssetsFormValues>
        control={control}
        options={options.MiscellaneousAssetOptions.ownerOptions}
        allowsUnequalOwnership={options.MiscellaneousAssetOptions.AllowUnequalOwnership}
        ownerModelFields={ownerModelFields}
        updateOwner={updateOwner}
        appendOwner={() => append({ ...APPEND_ASSET_OWNER })}
        removeOwner={(index: number) => remove(index)}
        setPercentage={setPercentage}
        error={errors?.ownerModel ? errors?.ownerModel[0]?.Id?.message : ''}
      />

      <Controller
        control={control}
        name="marketValue"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Market Value"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            formVariant={errors.marketValue && 'error'}
            helperText={errors.marketValue?.message}
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
            {...field}
          />
        )}
      />

      {isEstatePlanningWorkflow && !isOwnerTrust && <GrossEstateCheckbox control={control} />}

      {assetBucketingByManagementStyle && (
        <ManagementStyle<MiscAssetsFormValues>
          control={control}
          options={managementStylesOptions}
          formKey="orionManagementStyleId"
          onManagementSelect={onManagementSelect}
        />
      )}

      {displayAssetBucketing && (
        <AssetBucketSelect<MiscAssetsFormValues>
          control={control}
          formKey="customBucketIndex"
          isDisabled={
            (assetBucketingByManagementStyle && !allowAssetBucketingCustomization) ||
            (assetBucketingByManagementStyle && managementStyleSelection === null)
          }
          options={assetBucketOptions}
        />
      )}

      {isEstatePlanningWorkflow && (
        <Box>
          <Text as="label" variant="formLabel" color="neutral.600">
            Liquidity Status
          </Text>

          <Box display="flex" flexDirection="column" gridGap="8" mb="8">
            <Box>
              <Controller
                name="isLiquid"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <Radio name={name} checked={value} onChange={() => onChange(true)}>
                      Liquid
                    </Radio>
                    <Radio name={name} checked={!value} onChange={() => onChange(false)} mr="4">
                      Illiquid
                    </Radio>
                  </>
                )}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name="assetGrowth"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Asset Growth Rate"
                tooltip="The rate at which the asset’s market value will grow."
                rightIcon="far fa-percent"
                decimalScale={2}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue ?? null);
                }}
                {...field}
              />
            )}
          />

          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: ${({ theme }) => theme.space['16']}px;
            `}
          >
            <Controller
              name="saleYear"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Sale Year"
                  value={value ? ({ value, label: value } as any) : null}
                  options={saleYearOptions}
                  onChange={option => onChange(option?.value || null)}
                  tooltip="If applicable, enter the year this real estate asset will be sold."
                  isPortal
                  isClearable
                />
              )}
            />

            <Controller
              control={control}
              name="saleNetEarnings"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Net Proceeds from Sale"
                  leftIcon="far fa-dollar-sign"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  tooltip="If applicable, enter the estimated proceeds from the sale of this asset minus taxes and any fees to sell."
                  {...field}
                />
              )}
            />
          </Box>

          <Controller
            name="assetEarmarks"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                isMulti
                label="Asset Earmark"
                placeholder="Select a goal..."
                value={value}
                options={options.MiscellaneousAssetOptions.EarmarkOptions}
                onChange={earmarks => onChange(earmarks)}
                noOptionsMessage={() => 'There are no earmarks left to add'}
                closeMenuOnSelect={false}
                isPortal
                isClearable
              />
            )}
          />
        </>
      )}
    </form>
  );
};
